/** Default normal-proxy timeout. Preserves existing 30-second behavior when unset. */
export const DEFAULT_REQUEST_TIMEOUT_MS = 30000;

/** Maximum user-configured Request Timeout (Proxy Settings). */
export const MAX_REQUEST_TIMEOUT_MS = 5 * 60 * 1000;

/** Existing multipart/binary floor in proxyService (do not shorten uploads). */
export const MULTIPART_PROXY_TIMEOUT_FLOOR_MS = 30 * 60 * 1000;

/** Existing large-response bump for /api/users list responses. */
export const LARGE_RESPONSE_TIMEOUT_MS = 60000;

/** Hop-by-hop header: frontend → desktop agent. Never forwarded to the target API. */
export const PROXY_TIMEOUT_HEADER = 'X-Proxy-Timeout-Ms';

/** Query on the localhost proxy URL so timeout still arrives if CORS drops the header. */
export const PROXY_TIMEOUT_QUERY = 'proxyTimeoutMs';

/** Single stored setting key (milliseconds). */
export const REQUEST_TIMEOUT_STORAGE_KEY = 'requestTimeoutMs';

/** setTimeout stores delay as a signed 32-bit int; larger values wrap. */
const MAX_SETTIMEOUT_MS = 2147483647;

export type RequestTimeoutUnit = 'seconds' | 'minutes';

export function parseRequestTimeoutMs(raw: unknown): number | null {
  if (raw == null || raw === '') return null;
  const n = typeof raw === 'number' ? raw : Number(String(raw).trim());
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n > MAX_SETTIMEOUT_MS) return null;
  return Math.floor(n);
}

export function timeoutInputToMs(
  value: unknown,
  unit: RequestTimeoutUnit
): { ok: true; ms: number } | { ok: false; error: string } {
  if (value === '' || value == null) {
    return { ok: false, error: 'Timeout must be greater than 0.' };
  }
  const n = typeof value === 'number' ? value : Number(String(value).trim());
  if (!Number.isFinite(n) || Number.isNaN(n)) {
    return { ok: false, error: 'Timeout must be greater than 0.' };
  }
  if (n <= 0) {
    return { ok: false, error: 'Timeout must be greater than 0.' };
  }
  const ms = unit === 'minutes' ? n * 60_000 : n * 1000;
  if (ms > MAX_REQUEST_TIMEOUT_MS) {
    return { ok: false, error: 'Timeout cannot exceed 5 minutes.' };
  }
  const parsed = parseRequestTimeoutMs(ms);
  if (parsed == null) {
    return { ok: false, error: 'Timeout must be greater than 0.' };
  }
  return { ok: true, ms: parsed };
}

export function msToTimeoutDisplay(ms: number): { value: number; unit: RequestTimeoutUnit } {
  const n = parseRequestTimeoutMs(ms) ?? DEFAULT_REQUEST_TIMEOUT_MS;
  if (n >= 60000 && n % 60000 === 0) {
    return { value: n / 60000, unit: 'minutes' };
  }
  return { value: n / 1000, unit: 'seconds' };
}

/**
 * Effective timeout for one request.
 * Configured Proxy Settings value is the source of truth for the normal path.
 * Multipart/binary keeps the existing long floor. A longer special-case explicit
 * timeout (e.g. large-response 60s) can extend, never shorten below configured.
 * An explicit 30000 is treated as "unset" so leftover hardcoded callers cannot
 * hide a 30s cap when the user configured a longer timeout.
 */
export function resolveProxyFetchTimeoutMs(args: {
  configuredMs?: number | null;
  explicitMs?: number | null;
  isMultipartOrBinary?: boolean;
}): number {
  const configured = parseRequestTimeoutMs(args.configuredMs) ?? DEFAULT_REQUEST_TIMEOUT_MS;
  const explicit = parseRequestTimeoutMs(args.explicitMs);
  if (args.isMultipartOrBinary) {
    return Math.max(configured, explicit ?? 0, MULTIPART_PROXY_TIMEOUT_FLOOR_MS);
  }
  if (explicit != null && explicit !== DEFAULT_REQUEST_TIMEOUT_MS) {
    return Math.max(explicit, configured);
  }
  return configured;
}

export function snapshotRequestTimeoutMs(
  configuredMs: number | null | undefined,
  options?: { largeResponseExpected?: boolean; isMultipartOrBinary?: boolean }
): number {
  return resolveProxyFetchTimeoutMs({
    configuredMs,
    explicitMs: options?.largeResponseExpected ? LARGE_RESPONSE_TIMEOUT_MS : undefined,
    isMultipartOrBinary: options?.isMultipartOrBinary,
  });
}

export function formatRequestTimeoutLabel(ms: number): string {
  const n = parseRequestTimeoutMs(ms) ?? DEFAULT_REQUEST_TIMEOUT_MS;
  if (n % 60000 === 0) {
    const minutes = n / 60000;
    return minutes === 1 ? '1 minute' : `${minutes} minutes`;
  }
  if (n % 1000 === 0) {
    const seconds = n / 1000;
    return seconds === 1 ? '1 second' : `${seconds} seconds`;
  }
  return `${n} ms`;
}

export function formatRequestTimedOutError(ms: number): string {
  return `Request timed out after ${formatRequestTimeoutLabel(ms)}.`;
}

export function applyProxyTimeoutHeader(
  headers: Record<string, string>,
  timeoutMs: number
): Record<string, string> {
  return {
    ...headers,
    [PROXY_TIMEOUT_HEADER]: String(timeoutMs),
  };
}

export function appendProxyTimeoutQuery(proxyUrl: string, timeoutMs: number): string {
  const parsed = parseRequestTimeoutMs(timeoutMs);
  if (parsed == null) return proxyUrl;
  const hashIndex = proxyUrl.indexOf('#');
  const withoutHash = hashIndex >= 0 ? proxyUrl.slice(0, hashIndex) : proxyUrl;
  const hash = hashIndex >= 0 ? proxyUrl.slice(hashIndex) : '';
  const sep = withoutHash.includes('?') ? '&' : '?';
  return `${withoutHash}${sep}${PROXY_TIMEOUT_QUERY}=${encodeURIComponent(String(parsed))}${hash}`;
}

export function loadStoredRequestTimeoutMs(readCookie?: () => string | null): number {
  try {
    const fromLs = localStorage.getItem(REQUEST_TIMEOUT_STORAGE_KEY);
    const fromCookie = readCookie?.() ?? null;
    const loaded = parseRequestTimeoutMs(fromLs || fromCookie) ?? DEFAULT_REQUEST_TIMEOUT_MS;
    return Math.min(loaded, MAX_REQUEST_TIMEOUT_MS);
  } catch {
    return DEFAULT_REQUEST_TIMEOUT_MS;
  }
}

export function saveStoredRequestTimeoutMs(
  ms: number,
  writeCookie?: (ms: number) => void
): number {
  const parsed = parseRequestTimeoutMs(ms);
  if (parsed == null) {
    throw new Error('Timeout must be greater than 0.');
  }
  if (parsed > MAX_REQUEST_TIMEOUT_MS) {
    throw new Error('Timeout cannot exceed 5 minutes.');
  }
  localStorage.setItem(REQUEST_TIMEOUT_STORAGE_KEY, String(parsed));
  writeCookie?.(parsed);
  return parsed;
}
